import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Link,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth as useWorkOSAuth } from '@workos-inc/authkit-react';

// 2026-06-17: Phase 3 Increment 3 — the landing-page "front door". Username/password are gone;
// identity is handled by WorkOS (hosted page: Google or email/password). This component owns the
// WorkOS sign-in trigger and the bridge that exchanges the WorkOS token for a RothGPT session.
const CONNECTOR_URL = 'https://rothgpt-mcp.onrender.com/mcp';

const APP_FEATURES = ['Every number & chart', 'Tooltip explanations', 'Best for Power Users'];
const CLAUDE_FEATURES = ['Ask: "Run my Roth conversion analysis"', 'Claude explains it, at your level', 'Uses your Claude plan'];

const FeatureList = ({ items }) => (
  <List dense disablePadding>
    {items.map((f) => (
      <ListItem key={f} disableGutters sx={{ py: 0.5 }}>
        <ListItemIcon sx={{ minWidth: 28 }}>
          <CheckCircleIcon fontSize="small" sx={{ color: '#1e3a5f' }} />
        </ListItemIcon>
        <ListItemText primary={f} primaryTypographyProps={{ variant: 'body2', sx: { color: '#3a3f4a' } }} />
      </ListItem>
    ))}
  </List>
);

const LoginForm = ({ onWorkOSLogin }) => {
  const { user: workosUser, signIn: workosSignIn, getAccessToken: workosGetAccessToken } = useWorkOSAuth();
  const workosBridgedRef = React.useRef(false);
  const [claudeOpen, setClaudeOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(CONNECTOR_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // clipboard blocked — the URL field is selectable as a fallback
    }
  };

  // When WorkOS signs a user in (after the hosted-login redirect), exchange the access token for a
  // RothGPT session via onWorkOSLogin. Ref-guarded to bridge only once; skipped right after an
  // explicit logout so the user isn't immediately re-logged-in.
  React.useEffect(() => {
    if (!workosUser || workosBridgedRef.current || sessionStorage.getItem('workosLoggedOut')) return;
    workosBridgedRef.current = true;
    workosGetAccessToken()
      .then((token) => onWorkOSLogin && onWorkOSLogin(token))
      .catch((e) => {
        workosBridgedRef.current = false;
        console.error('[WorkOS] login bridge failed', e);
      });
  }, [workosUser, workosGetAccessToken, onWorkOSLogin]);

  const launchApp = () => {
    sessionStorage.removeItem('workosLoggedOut');  // fresh sign-in clears the logout guard
    workosSignIn();
  };

  return (
    <Container maxWidth="md" sx={{ mt: 1, mb: 2 }}>
      <Typography variant="h5" sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: '#16263f', mb: 2 }}>
        How would you like to use RothGPT?
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'stretch' }}>
        {/* Left — Launch Full App (WorkOS login → dashboard) */}
        <Card variant="outlined" sx={{ flex: '1 1 320px', maxWidth: 380, display: 'flex', flexDirection: 'column', borderColor: '#1e3a5f', borderWidth: 2, borderRadius: 2, bgcolor: '#ffffff' }}>
          <CardContent sx={{ flexGrow: 1, textAlign: 'left', p: 3 }}>
            <Typography variant="h6" sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: '#16263f', mb: 1 }}>
              The Full Web App
            </Typography>
            <FeatureList items={APP_FEATURES} />
          </CardContent>
          <Box sx={{ p: 3, pt: 0 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={launchApp}
              sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, py: 1, color: '#1e3a5f', borderColor: '#1e3a5f', '&:hover': { borderColor: '#16263f', bgcolor: 'rgba(30,58,95,0.04)' } }}
            >
              Open RothGPT Here
            </Button>
          </Box>
        </Card>

        {/* Right — Chat Inside Claude (add-connector steps) */}
        <Card variant="outlined" sx={{ flex: '1 1 320px', maxWidth: 380, display: 'flex', flexDirection: 'column', borderColor: '#1e3a5f', borderWidth: 2, borderRadius: 2, bgcolor: '#ffffff' }}>
          <CardContent sx={{ flexGrow: 1, textAlign: 'left', p: 3 }}>
            <Typography variant="h6" sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: '#16263f', mb: 1 }}>
              Guided by Claude AI
            </Typography>
            <FeatureList items={CLAUDE_FEATURES} />
          </CardContent>
          <Box sx={{ p: 3, pt: 0 }}>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={() => setClaudeOpen(true)}
              sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, py: 1, bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#16263f' } }}
            >
              Use RothGPT in Claude
            </Button>
          </Box>
        </Card>
      </Box>

      <Typography sx={{ mt: 2.5, fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: '#16263f', fontSize: '1.1rem' }}>
        Same account &amp; data either way — log in once.
      </Typography>

      {/* "Add RothGPT to Claude" steps */}
      <Dialog open={claudeOpen} onClose={() => setClaudeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: '#16263f' }}>Connect to RothGPT in Claude</DialogTitle>
        <DialogContent sx={{ textAlign: 'left' }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Chat with RothGPT inside Claude (Pro, Max, or Team plan):
          </Typography>

          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>1.</strong> Open Claude's connectors, then click <strong>+</strong> (Add custom connector):
          </Typography>
          <Button
            variant="contained"
            size="small"
            disableElevation
            href="https://claude.ai/customize/connectors"
            target="_blank"
            rel="noopener"
            sx={{ mb: 1.5, textTransform: 'none', bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#16263f' } }}
          >
            Open Claude Connectors
          </Button>

          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>2.</strong> Paste this URL into the "Add custom connector" box:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <TextField value={CONNECTOR_URL} size="small" fullWidth InputProps={{ readOnly: true }} />
            <Button variant="outlined" size="small" onClick={handleCopyUrl} sx={{ flexShrink: 0, textTransform: 'none', color: '#1e3a5f', borderColor: '#1e3a5f', '&:hover': { borderColor: '#16263f', bgcolor: 'rgba(30,58,95,0.04)' } }}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </Box>

          <Typography variant="body2">
            <strong>3.</strong> Sign in with Google or email when Claude prompts, then ask
            &ldquo;Show my RothGPT inputs&rdquo; to begin.
          </Typography>

          <Typography variant="body2" sx={{ mt: 1.5 }}>
            <strong>Already added it?</strong> Just open{' '}
            <Link href="https://claude.ai/new" target="_blank" rel="noopener" sx={{ color: '#1e3a5f', fontWeight: 600 }}>Claude</Link>
            {' '}and ask &ldquo;Run my Roth conversion analysis.&rdquo;
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClaudeOpen(false)} sx={{ color: '#1e3a5f', textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LoginForm;
